import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Check, ChevronLeft, ChevronRight, Copy} from 'lucide-react';
import styles from '../VLAOperators/VLAAdminShared.module.css';
import {PRISMAX_BACKEND_URL, PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';

const CHAIN_COLORS = {
    Solana: {bg: '#1a2a1a', color: '#4caf7d'},
    Ethereum: {bg: '#1a1a2e', color: '#7b8fd4'},
    Base: {bg: '#0f1e2e', color: '#4e8fd4'},
    Monad: {bg: '#1a1030', color: '#a07bd4'},
    Aptos: {bg: '#0f2a20', color: '#4ccfa0'},
};

const QA_ROLES = [
    {value: 'qa', label: 'QA'},
    {value: 'senior qa', label: 'Senior QA'},
    {value: 'expert qa', label: 'Expert QA'},
];

const LOOKUP_TYPES = [
    {value: 'email', label: 'Email'},
    {value: 'wallet', label: 'Wallet address'},
];

const BLOCKLIST_LOOKUP_TYPES = [
    {value: 'user_id', label: 'User ID'},
    {value: 'email', label: 'Email'},
    {value: 'wallet', label: 'Wallet address'},
];

const QA_BLOCKLIST_DURATION_DAYS = 30;

const WALLET_CHAINS = [
    {value: 'solana', label: 'solana'},
    {value: 'monad', label: 'monad'},
    {value: 'base', label: 'base'},
    {value: 'ethereum', label: 'ethereum'},
];

const formatScore = (value) => {
    const score = Number(value);
    if (!Number.isFinite(score)) return '—';
    return score.toFixed(1);
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const getReviewerQaRole = (reviewer) => String(
    reviewer?.qa_role ?? reviewer?.user_role ?? ''
).trim().toLowerCase();

const CopyButton = ({text}) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        });
    };
    return (
        <button className={styles.copyButton} onClick={handleCopy} title="Copy">
            {copied
                ? <Check size={13} className={styles.copyIconDone}/>
                : <Copy size={13} className={styles.copyIcon}/>}
        </button>
    );
};

const UserCell = ({reviewer}) => {
    if (reviewer.wallet_address) {
        const truncated = `${reviewer.wallet_address.slice(0, 6)}...${reviewer.wallet_address.slice(-6)}`;
        const chainStyle = CHAIN_COLORS[reviewer.wallet_chain] || {bg: '#2a2a2a', color: '#aaaaaa'};
        return (
            <div className={styles.userCell}>
                <div className={styles.walletAddressRow}>
                    <span className={styles.walletAddress} title={reviewer.wallet_address}>{truncated}</span>
                    <CopyButton text={reviewer.wallet_address}/>
                </div>
                {reviewer.wallet_chain && (
                    <span className={styles.chainBadge} style={{background: chainStyle.bg, color: chainStyle.color}}>
                        {reviewer.wallet_chain}
                    </span>
                )}
                {reviewer.email && <div className={styles.userSecondary}>{reviewer.email}</div>}
                <div className={styles.userId}>ID: {reviewer.user_id}</div>
            </div>
        );
    }

    return (
        <div className={styles.userCell}>
            <div className={styles.userPrimary}>{reviewer.email || '—'}</div>
            <div className={styles.userId}>ID: {reviewer.user_id}</div>
        </div>
    );
};

const Pagination = ({page, totalPages, onChange}) => {
    if (totalPages <= 1) return null;
    return (
        <div className={styles.pagination}>
            <button className={styles.pageButton} onClick={() => onChange(page - 1)} disabled={page <= 1}>
                <ChevronLeft size={14}/>
            </button>
            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
            <button className={styles.pageButton} onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
                <ChevronRight size={14}/>
            </button>
        </div>
    );
};

const ConfirmModal = ({action, processing, onCancel, onConfirm}) => {
    if (!action) return null;
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <h3 className={styles.modalTitle}>{action.title}</h3>
                <p className={styles.modalText}>{action.message}</p>
                {action.error && <p className={styles.errorMsg}>{action.error}</p>}
                <div className={styles.modalActions}>
                    <button className={styles.cancelButton} onClick={onCancel} disabled={processing}>Cancel</button>
                    <button className={styles.confirmButton} onClick={onConfirm} disabled={processing}>
                        {processing ? 'Saving...' : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AddReviewerModal = ({form, setForm, onCancel, onSubmit}) => (
    <div className={styles.modalOverlay}>
        <form className={styles.modal} onSubmit={onSubmit}>
            <h3 className={styles.modalTitle}>Add QA reviewer</h3>
            <label className={styles.formLabel}>
                Lookup by
                <select
                    className={styles.formSelect}
                    value={form.lookup_type}
                    onChange={(event) => setForm((current) => ({...current, lookup_type: event.target.value}))}
                >
                    {LOOKUP_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </label>
            {form.lookup_type === 'wallet' && (
                <label className={styles.formLabel}>
                    Wallet chain
                    <select
                        className={styles.formSelect}
                        value={form.wallet_chain}
                        onChange={(event) => setForm((current) => ({...current, wallet_chain: event.target.value}))}
                    >
                        {WALLET_CHAINS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            )}
            <label className={styles.formLabel}>
                Email or wallet address
                <input
                    className={styles.formInput}
                    value={form.identifier}
                    onChange={(event) => setForm((current) => ({...current, identifier: event.target.value}))}
                    placeholder="user@email.com or wallet address"
                    autoFocus
                />
            </label>
            <label className={styles.formLabel}>
                QA level
                <select
                    className={styles.formSelect}
                    value={form.qa_role}
                    onChange={(event) => setForm((current) => ({...current, qa_role: event.target.value}))}
                >
                    {QA_ROLES.map((role) => (
                        <option key={role.value} value={role.value} disabled={role.disabled}>{role.label}</option>
                    ))}
                </select>
            </label>
            <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={onCancel}>Cancel</button>
                <button type="submit" className={styles.confirmButton}>Add</button>
            </div>
        </form>
    </div>
);

const AddToBlocklistModal = ({form, setForm, onCancel, onSubmit}) => (
    <div className={styles.modalOverlay}>
        <form className={styles.modal} onSubmit={onSubmit}>
            <h3 className={styles.modalTitle}>Add to blocklist</h3>
            <label className={styles.formLabel}>
                Lookup by
                <select
                    className={styles.formSelect}
                    value={form.lookup_type}
                    onChange={(event) => setForm((current) => ({...current, lookup_type: event.target.value}))}
                >
                    {BLOCKLIST_LOOKUP_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </label>
            {form.lookup_type === 'wallet' && (
                <label className={styles.formLabel}>
                    Wallet chain
                    <select
                        className={styles.formSelect}
                        value={form.wallet_chain}
                        onChange={(event) => setForm((current) => ({...current, wallet_chain: event.target.value}))}
                    >
                        {WALLET_CHAINS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>
            )}
            <label className={styles.formLabel}>
                {form.lookup_type === 'user_id' ? 'User ID' : 'Email or wallet address'}
                <input
                    className={styles.formInput}
                    value={form.identifier}
                    onChange={(event) => setForm((current) => ({...current, identifier: event.target.value}))}
                    placeholder={form.lookup_type === 'user_id' ? 'e.g. 1024' : 'user@email.com or wallet address'}
                    autoFocus
                />
            </label>
            <label className={styles.formLabel}>
                Reason (optional)
                <input
                    className={styles.formInput}
                    value={form.blocked_reason}
                    onChange={(event) => setForm((current) => ({...current, blocked_reason: event.target.value}))}
                    placeholder="Why is this user being blocked?"
                />
            </label>
            <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={onCancel}>Cancel</button>
                <button type="submit" className={styles.confirmButton}>Add</button>
            </div>
        </form>
    </div>
);

const QAReviewersBlocklist = () => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [entries, setEntries] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [collapsed, setCollapsed] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({lookup_type: 'user_id', wallet_chain: 'solana', identifier: '', blocked_reason: ''});
    const [confirmAction, setConfirmAction] = useState(null);
    const [processingAction, setProcessingAction] = useState(false);
    const [processingId, setProcessingId] = useState(null);

    const getToken = () => localStorage.getItem('prismax_admin_token');

    const fetchPage = useCallback(async (nextPage) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/blocklist?page=${nextPage}`,
                {headers: {Authorization: `Bearer ${getToken()}`}},
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(getApiErrorMessage(result, 'Failed to load blocklist.'));
            }
            setEntries(Array.isArray(result.data) ? result.data : []);
            setPage(Number(result.pagination?.page || nextPage));
            setTotalPages(Number(result.pagination?.total_pages || 1));
            setTotalCount(Number(result.pagination?.total_count || 0));
        } catch (fetchError) {
            setEntries([]);
            if (isSessionExpiredError(fetchError)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError(fetchError?.message || 'Failed to load blocklist.');
        } finally {
            setLoading(false);
        }
    }, [handleAdminSessionExpired]);

    useEffect(() => {
        void fetchPage(1);
    }, [fetchPage]);

    const closeAddModal = () => {
        setShowAddModal(false);
        setAddForm({lookup_type: 'user_id', wallet_chain: 'solana', identifier: '', blocked_reason: ''});
    };

    const handleAddSubmit = (event) => {
        event.preventDefault();
        const identifier = addForm.identifier.trim();
        if (!identifier) {
            setConfirmAction({
                title: 'Missing user',
                message: 'Enter a user ID, email, or wallet address before blocking a user.',
                payload: null,
                error: '',
            });
            return;
        }
        setConfirmAction({
            title: 'Add to blocklist?',
            message: `Block ${identifier} for ${QA_BLOCKLIST_DURATION_DAYS} days?`,
            payload: {
                lookup_type: addForm.lookup_type,
                wallet_chain: addForm.lookup_type === 'wallet' ? addForm.wallet_chain : undefined,
                identifier,
                blocked_reason: addForm.blocked_reason.trim() || undefined,
            },
            action: 'add',
            afterSuccess: closeAddModal,
        });
    };

    const requestReinstate = (entry) => {
        setConfirmAction({
            title: 'Reinstate user?',
            message: `Reinstate user ${entry.user_id} now?`,
            payload: {qa_blocklist_id: entry.qa_blocklist_id},
            action: 'reinstate',
            processingId: entry.qa_blocklist_id,
        });
    };

    const handleConfirm = async () => {
        if (!confirmAction?.payload) {
            setConfirmAction(null);
            return;
        }
        setProcessingAction(true);
        setProcessingId(confirmAction.processingId || null);
        try {
            const endpoint = confirmAction.action === 'reinstate'
                ? `${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/blocklist/reinstate`
                : `${PRISMAX_DATA_PIPELINE_URL}/data/admin/qa-review/blocklist/add`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(confirmAction.payload),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(getApiErrorMessage(result, 'Failed to update blocklist.'));
            }
            if (confirmAction.afterSuccess) confirmAction.afterSuccess();
            setConfirmAction(null);
            await fetchPage(page);
        } catch (updateError) {
            if (isSessionExpiredError(updateError)) {
                handleAdminSessionExpired?.();
                return;
            }
            setConfirmAction((current) => ({
                ...current,
                error: updateError?.message || 'Failed to update blocklist.',
            }));
        } finally {
            setProcessingAction(false);
            setProcessingId(null);
        }
    };

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                    QA Reviewers Blocklist
                    <span className={styles.count}>{totalCount}</span>
                </h2>
                <button className={styles.collapseButton} onClick={() => setCollapsed((prev) => !prev)}>
                    {collapsed ? 'Show' : 'Hide'}
                </button>
                <button className={styles.addButton} onClick={() => setShowAddModal(true)}>Add to Blocklist</button>
            </div>

            {!collapsed && (
                <>
                    {loading && <p className={styles.stateMsg}>Loading...</p>}
                    {error && <p className={styles.errorMsg}>{error}</p>}
                    {!loading && !error && entries.length === 0 && (
                        <p className={styles.empty}>No blocked users.</p>
                    )}
                    {!loading && !error && entries.length > 0 && (
                        <>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <colgroup>
                                        <col/>
                                        <col style={{width: '160px'}}/>
                                        <col/>
                                        <col style={{width: '160px'}}/>
                                        <col style={{width: '140px'}}/>
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className={styles.th}>User</th>
                                            <th className={styles.th}>Blocked At</th>
                                            <th className={styles.th}>Reason</th>
                                            <th className={styles.th}>Reinstates On</th>
                                            <th className={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((entry) => (
                                            <tr key={entry.qa_blocklist_id} className={styles.row}>
                                                <td className={styles.cell}><UserCell reviewer={entry}/></td>
                                                <td className={styles.cell}>{formatDate(entry.blocked_at)}</td>
                                                <td className={styles.cell}>{entry.blocked_reason || '—'}</td>
                                                <td className={styles.cell}>{formatDate(entry.desired_reinstated_at)}</td>
                                                <td className={`${styles.cell} ${styles.actionCell}`}>
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={() => requestReinstate(entry)}
                                                        disabled={processingAction || processingId === entry.qa_blocklist_id}
                                                    >
                                                        Reinstate
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination page={page} totalPages={totalPages} onChange={fetchPage}/>
                        </>
                    )}
                </>
            )}

            {showAddModal && (
                <AddToBlocklistModal
                    form={addForm}
                    setForm={setAddForm}
                    onCancel={closeAddModal}
                    onSubmit={handleAddSubmit}
                />
            )}
            <ConfirmModal
                action={confirmAction}
                processing={processingAction}
                onCancel={() => setConfirmAction(null)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

const VLAAdminQAReviewers = () => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [reviewers, setReviewers] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [addForm, setAddForm] = useState({lookup_type: 'email', wallet_chain: 'solana', identifier: '', qa_role: 'qa'});
    const [confirmAction, setConfirmAction] = useState(null);
    const [processingAction, setProcessingAction] = useState(false);
    const [processingUserId, setProcessingUserId] = useState(null);

    const getToken = () => localStorage.getItem('prismax_admin_token');

    const fetchPage = useCallback(async (nextPage) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/admin/get-qa-reviewers?page=${nextPage}`,
                {headers: {Authorization: `Bearer ${getToken()}`}},
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(getApiErrorMessage(result, 'Failed to load QA reviewers.'));
            }
            setReviewers(Array.isArray(result.data) ? result.data : []);
            setPage(Number(result.pagination?.page || nextPage));
            setTotalPages(Number(result.pagination?.total_pages || 1));
            setTotalCount(Number(result.pagination?.total_count || 0));
        } catch (fetchError) {
            setReviewers([]);
            if (isSessionExpiredError(fetchError)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError(fetchError?.message || 'Failed to load QA reviewers.');
        } finally {
            setLoading(false);
        }
    }, [handleAdminSessionExpired]);

    useEffect(() => {
        void fetchPage(1);
    }, [fetchPage]);

    const postRoleUpdate = async (payload) => {
        const response = await fetch(`${PRISMAX_BACKEND_URL}/api/admin/set-qa-reviewer-role`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${getToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
            throw new Error(getApiErrorMessage(result, 'Failed to update QA role.'));
        }
        return result;
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setAddForm({lookup_type: 'email', wallet_chain: 'solana', identifier: '', qa_role: 'qa'});
    };

    const handleAddSubmit = (event) => {
        event.preventDefault();
        const identifier = addForm.identifier.trim();
        if (!identifier) {
            setConfirmAction({
                title: 'Missing user',
                message: 'Enter an email or wallet address before adding a QA reviewer.',
                payload: null,
                error: '',
            });
            return;
        }
        setConfirmAction({
            title: 'Add QA reviewer?',
            message: `Set ${identifier} to ${addForm.qa_role}?`,
            payload: {
                lookup_type: addForm.lookup_type === 'wallet' ? addForm.wallet_chain : 'email',
                identifier,
                qa_role: addForm.qa_role,
                user_role: addForm.qa_role,
            },
            refreshPage: 1,
            afterSuccess: closeAddModal,
        });
    };

    const requestRoleChange = (reviewer, nextRole) => {
        const currentRole = getReviewerQaRole(reviewer);
        if (!nextRole || nextRole === currentRole) return;
        setConfirmAction({
            title: 'Change QA level?',
            message: `Change user ${reviewer.user_id} from ${currentRole || 'none'} to ${nextRole}?`,
            payload: {
                user_id: reviewer.user_id,
                qa_role: nextRole,
                user_role: nextRole,
            },
            refreshPage: page,
            processingUserId: reviewer.user_id,
        });
    };

    const requestRemove = (reviewer) => {
        setConfirmAction({
            title: 'Remove QA reviewer?',
            message: `Clear QA role for user ${reviewer.user_id}?`,
            payload: {
                user_id: reviewer.user_id,
                qa_role: null,
                user_role: null,
            },
            refreshPage: page,
            processingUserId: reviewer.user_id,
        });
    };

    const handleConfirm = async () => {
        if (!confirmAction?.payload) {
            setConfirmAction(null);
            return;
        }
        setProcessingAction(true);
        setProcessingUserId(confirmAction.processingUserId || null);
        try {
            await postRoleUpdate(confirmAction.payload);
            if (confirmAction.afterSuccess) confirmAction.afterSuccess();
            const nextPage = confirmAction.refreshPage || page;
            setConfirmAction(null);
            await fetchPage(nextPage);
        } catch (updateError) {
            if (isSessionExpiredError(updateError)) {
                handleAdminSessionExpired?.();
                return;
            }
            setConfirmAction((current) => ({
                ...current,
                error: updateError?.message || 'Failed to update QA role.',
            }));
        } finally {
            setProcessingAction(false);
            setProcessingUserId(null);
        }
    };

    return (
        <>
        <div className={styles.section}>
            <div className={styles.qaTitleRow}>
                <h2 className={`${styles.title} ${styles.titleWithCount}`}>
                    QA Reviewers
                    <span className={styles.count}>{totalCount}</span>
                </h2>
                <button className={styles.addButton} onClick={() => setShowAddModal(true)}>Add</button>
            </div>

            {loading && <p className={styles.stateMsg}>Loading...</p>}
            {error && <p className={styles.errorMsg}>{error}</p>}
            {!loading && !error && reviewers.length === 0 && (
                <p className={styles.empty}>No QA reviewers.</p>
            )}
            {!loading && !error && reviewers.length > 0 && (
                <>
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <colgroup>
                                <col/>
                                <col style={{width: '260px'}}/>
                                <col style={{width: '160px'}}/>
                                <col style={{width: '160px'}}/>
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className={styles.th}>User</th>
                                    <th className={styles.th}>Role</th>
                                    <th className={styles.th}>Reviews</th>
                                    <th className={styles.th}>Average Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewers.map((reviewer) => (
                                    <tr key={reviewer.user_id} className={styles.row}>
                                        <td className={styles.cell}><UserCell reviewer={reviewer}/></td>
                                        <td className={styles.cell}>
                                            <div className={styles.qaRoleControls}>
                                                <select
                                                    className={styles.roleSelect}
                                                    value={getReviewerQaRole(reviewer)}
                                                    onChange={(event) => requestRoleChange(reviewer, event.target.value)}
                                                    disabled={processingAction || processingUserId === reviewer.user_id}
                                                >
                                                    {QA_ROLES.map((role) => (
                                                        <option key={role.value} value={role.value} disabled={role.disabled}>{role.label}</option>
                                                    ))}
                                                </select>
                                                {reviewer.is_operator && (
                                                    <span className={styles.chainBadge}>Operator</span>
                                                )}
                                                <button
                                                    className={styles.removeButton}
                                                    onClick={() => requestRemove(reviewer)}
                                                    disabled={processingAction || processingUserId === reviewer.user_id}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                        <td className={styles.cell}>
                                            <span className={styles.metricValue}>{Number(reviewer.review_count || 0).toLocaleString('en-US')}</span>
                                        </td>
                                        <td className={styles.cell}>
                                            <span className={styles.metricValue}>{formatScore(reviewer.average_score)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} totalPages={totalPages} onChange={fetchPage}/>
                </>
            )}
            {showAddModal && (
                <AddReviewerModal
                    form={addForm}
                    setForm={setAddForm}
                    onCancel={closeAddModal}
                    onSubmit={handleAddSubmit}
                />
            )}
            <ConfirmModal
                action={confirmAction}
                processing={processingAction}
                onCancel={() => setConfirmAction(null)}
                onConfirm={handleConfirm}
            />
        </div>
        <QAReviewersBlocklist/>
        </>
    );
};

export default VLAAdminQAReviewers;
